import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Sparkle, X, PaperPlaneRight, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Floating AI chat editor for admins + leaders.
 * Open → type natural-language commands like "set Backd min credit to 600"
 * → Gemini parses the intent and the backend applies the edit live.
 */
export default function AiChatWidget() {
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || user?.role === "leader";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey 👋 — tell me what to change. Add, remove, single or bulk:\n• 'set Backd min credit to 600'\n• 'add Tier 2A to Lendr, Kapitus, and Vox'\n• 'remove cannabis from Backd restricted industries'\n• 'remove trucking from restricted on every Tier 1 lender'\n• 'clear all restricted industries on Lendr'" },
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  if (!isPrivileged) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        message: text,
        history: messages.slice(-6),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "Done.", meta: data }]);
      if (data.applied) toast.success(data.reply);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: `Error: ${err.response?.data?.detail || "AI unreachable"}` }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 pointer-events-none">
      {open && (
        <div className="pointer-events-auto w-[360px] max-h-[70vh] mb-3 rounded-xl shadow-2xl bg-white border border-slate-200 overflow-hidden flex flex-col animate-in slide-in-from-bottom-2 fade-in" data-testid="ai-chat-panel">
          <div className="px-4 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-cyan-300" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-300">AI Editor</div>
              <div className="text-sm font-semibold">Quick lender changes</div>
            </div>
            <button onClick={() => setOpen(false)} className="h-7 w-7 grid place-items-center rounded-md hover:bg-white/10 text-slate-300 hover:text-white" data-testid="ai-chat-close">
              <X size={14} />
            </button>
          </div>
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-slate-900 text-white" : "bg-white text-slate-800 border border-slate-200"
                }`}>
                  {m.content}
                  {m.meta?.applied && m.meta?.lender_id && (
                    <a href={`/lenders/${m.meta.lender_id}`} target="_blank" rel="noopener noreferrer" className="block mt-1.5 text-[11px] text-blue-600 hover:underline">
                      View lender →
                    </a>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
                  thinking…
                </div>
              </div>
            )}
          </div>
          <form onSubmit={send} className="p-2.5 border-t border-slate-200 flex items-end gap-2 bg-white">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Tell me what to change…"
              className="flex-1 resize-none px-3 py-2 text-sm border border-slate-300 rounded-md focus:border-slate-900 focus:ring-2 focus:ring-slate-200 outline-none max-h-32"
              data-testid="ai-chat-input"
            />
            <Button type="submit" disabled={!draft.trim() || busy} className="bg-slate-900 hover:bg-slate-800 text-white shrink-0" data-testid="ai-chat-send">
              <PaperPlaneRight size={14} weight="fill" />
            </Button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-chat-toggle"
        className="pointer-events-auto h-14 w-14 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600 text-white shadow-2xl grid place-items-center transition-transform hover:scale-105 ring-4 ring-cyan-500/20"
        title="AI Editor"
      >
        {open ? <X size={20} weight="bold" /> : <Lightning size={22} weight="fill" className="text-cyan-300" />}
      </button>
    </div>
  );
}
